import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtGHS, pName } from "@/lib/format";
import { Search, Trash2, ShoppingCart, Plus, Minus, ReceiptText, UserPlus, PackageX, Printer } from "lucide-react";
import { printReceipt } from "@/lib/receipt";
import { ShiftSummary } from "@/components/ShiftSummary";
import { MissedSaleDialog } from "@/components/MissedSaleDialog";
import { ScanBox } from "@/components/ScanBox";
import { LocationPicker } from "@/components/LocationPicker";
import { toast } from "sonner";

const nowLocalDatetime = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export default function POS() {
  const { branches, activeBranch, changeBranch } = useBranch();
  const [branchId, setBranchId] = useState(activeBranch !== "all" ? activeBranch : "");
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerType, setCustomerType] = useState("cash");
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [memberId, setMemberId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [tendered, setTendered] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [closedShift, setClosedShift] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [schemes, setSchemes] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [missedOpen, setMissedOpen] = useState(false);
  const [missedPrefill, setMissedPrefill] = useState(null);
  const openMissed = (p) => { setMissedPrefill(p || null); setMissedOpen(true); };
  const [nearExpiry, setNearExpiry] = useState({ product_ids: [], suggested_discount: 20 });
  const [custOpen, setCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", insurance_scheme_id: "", scheme_member_no: "", credit_limit: 0 });
  const [savingCust, setSavingCust] = useState(false);
  const [fulfillment, setFulfillment] = useState("pickup");
  const [deliveryAddr, setDeliveryAddr] = useState("");
  const [deliveryPhone, setDeliveryPhone] = useState("");
  const [deliveryFee, setDeliveryFee] = useState("");
  const [deliveryLat, setDeliveryLat] = useState(null);
  const [deliveryLng, setDeliveryLng] = useState(null);
  const [scheduledFor, setScheduledFor] = useState(nowLocalDatetime);
  const [searchParams] = useSearchParams();
  const [pendingCustomerId, setPendingCustomerId] = useState("");
  const appliedRx = useRef(false);

  // ── shift session ──
  const [shift, setShift] = useState(null);
  const [openShiftDlg, setOpenShiftDlg] = useState(false);
  const [closeShiftDlg, setCloseShiftDlg] = useState(false);
  const [shiftLabel, setShiftLabel] = useState("Morning");
  const [openingFloat, setOpeningFloat] = useState("");
  const [countedCash, setCountedCash] = useState("");
  const [closeNote, setCloseNote] = useState("");

  const loadShift = () => {
    if (!branchId) { setShift(null); return; }
    api.get("/shifts/current", { params: { branch_id: branchId } }).then((r) => setShift(r.data || null)).catch(() => setShift(null));
  };
  useEffect(() => { loadShift(); }, [branchId]); // eslint-disable-line

  const doOpenShift = async () => {
    try {
      const { data } = await api.post("/shifts/open", { branch_id: branchId, shift_label: shiftLabel, opening_float: Number(openingFloat) || 0 });
      setShift({ ...data, totals: { sales_count: 0, cash: 0, card: 0, momo: 0, insurance: 0, corporate_credit: 0 } });
      setOpenShiftDlg(false); setOpeningFloat("");
      toast.success(`${data.code} — ${data.shift_label} shift opened`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const openCloseDlg = async () => {
    try { const { data } = await api.get(`/shifts/${shift.id}`); setShift(data); } catch (e) { /* keep */ }
    setCountedCash(""); setCloseNote(""); setCloseShiftDlg(true);
  };
  const doCloseShift = async () => {
    try {
      const { data } = await api.post(`/shifts/${shift.id}/close`, { counted_cash: Number(countedCash) || 0, note: closeNote });
      toast.success(`Shift closed — variance ${fmtGHS(data.variance)}`);
      setCloseShiftDlg(false); setShift(null); setClosedShift(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const expectedCash = shift ? (Number(shift.opening_float || 0) + Number(shift.totals?.cash || 0)) : 0;
  const liveVariance = (Number(countedCash) || 0) - expectedCash;

  const loadCustomers = () =>
    api.get("/customers", { params: { type: customerType === "cash" ? undefined : customerType } })
      .then((r) => setCustomers(r.data));

  const openAddCustomer = () => {
    setNewCust({ name: "", phone: "", insurance_scheme_id: "", scheme_member_no: "", credit_limit: 0 });
    setCustOpen(true);
  };

  const saveNewCustomer = async () => {
    if (!newCust.name.trim()) return toast.error("Enter a customer name");
    if (customerType === "insurance" && !newCust.insurance_scheme_id) return toast.error("Select an insurance scheme");
    setSavingCust(true);
    try {
      const { data } = await api.post("/customers", {
        type: customerType, name: newCust.name, phone: newCust.phone,
        insurance_scheme_id: newCust.insurance_scheme_id || null,
        scheme_member_no: newCust.scheme_member_no,
        credit_limit: Number(newCust.credit_limit) || 0,
      });
      await loadCustomers();
      setCustomerId(data.id);
      setCustOpen(false);
      toast.success(`${newCust.name} added`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingCust(false); }
  };

  useEffect(() => {
    const first = branches.find((x) => x.is_active !== false);
    if (!branchId && first) setBranchId(activeBranch !== "all" ? activeBranch : first.id);
  }, [branches, activeBranch]); // eslint-disable-line

  useEffect(() => { api.get("/insurance-schemes").then((r) => setSchemes(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!branchId) return;
    api.get("/stock", { params: { branch_id: branchId } })
      .then((r) => setStockMap(Object.fromEntries(r.data.map((s) => [s.product_id, s.total_qty]))))
      .catch(() => setStockMap({}));
    api.get("/near-expiry-products", { params: { branch_id: branchId } })
      .then((r) => setNearExpiry(r.data)).catch(() => setNearExpiry({ product_ids: [], suggested_discount: 20 }));
  }, [branchId, receipt]);
  const isNear = (id) => nearExpiry.product_ids?.includes(id);
  const eff = (l) => l.unit_price * (1 - (l.discount || 0) / 100);
  const setDiscount = (id, v) => setCart((c) => c.map((l) => l.product_id === id ? { ...l, discount: Math.max(0, Math.min(Number(nearExpiry.max_discount ?? 100), Number(v) || 0)) } : l));

  useEffect(() => {
    api.get("/products", { params: { q: q || undefined } }).then((r) => setProducts(r.data.filter((p) => p.is_active)));
  }, [q]);

  useEffect(() => {
    api.get("/customers", { params: { type: customerType === "cash" ? undefined : customerType } }).then((r) => setCustomers(r.data));
    setCustomerId("");
  }, [customerType]);

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const activeMembers = customerType === "corporate" ? (selectedCustomer?.members || []).filter((m) => m.is_active ?? true) : [];
  useEffect(() => { setMemberId(""); }, [customerId]);
  const schemeMarkup = schemes.find((s) => s.id === selectedCustomer?.insurance_scheme_id)?.markup_percent;
  const priceFor = (p) => {
    if (customerType === "corporate") return p.price_corporate ?? p.price_cash;
    if (customerType === "insurance" && schemeMarkup != null)
      return Math.round(Math.round((p.cost_price || 0) * (1 + schemeMarkup / 100) * 1000) / 100) / 10;
    return p.price_cash;
  };

  const addToCart = (p) => {
    if (!shift) { toast.warning("Open your shift first to start selling"); setShiftLabel("Morning"); setOpeningFloat(""); setOpenShiftDlg(true); return; }
    setCart((c) => {
      const ex = c.find((l) => l.product_id === p.id);
      if (ex) return c.map((l) => l.product_id === p.id ? { ...l, quantity: l.quantity + 1 } : l);
      return [...c, { product_id: p.id, sku: p.sku, name: pName(p), schedule: p.drug_schedule, unit_price: priceFor(p), quantity: 1, covered: false, discount: isNear(p.id) ? Number(nearExpiry.suggested_discount) || 0 : 0 }];
    });
  };
  const handleScan = async (code) => {
    let p = products.find((x) => x.barcode && x.barcode === code);
    if (!p) {
      try { const { data } = await api.get("/products", { params: { barcode: code } }); p = (data || []).filter((x) => x.is_active)[0] || (data || [])[0]; } catch { /* ignore */ }
    }
    if (!p) { toast.error(`No product matches barcode ${code}`); return; }
    const qty = stockMap[p.id] ?? 0;
    if (qty <= 0) { openMissed(p); toast.warning(`${pName(p)} is out of stock — log a missed sale`); return; }
    addToCart(p);
    toast.success(`Added ${pName(p)}`);
  };

  const setQty = (id, d) => setCart((c) => c.map((l) => l.product_id === id ? { ...l, quantity: Math.max(1, l.quantity + d) } : l));
  const setQtyVal = (id, v) => setCart((c) => c.map((l) => l.product_id === id ? { ...l, quantity: Math.max(1, Number(v) || 1) } : l));
  const toggleCovered = (id) => setCart((c) => c.map((l) => l.product_id === id ? { ...l, covered: !l.covered, insCover: "" } : l));
  const setInsCover = (id, v) => setCart((c) => c.map((l) => l.product_id === id ? { ...l, insCover: v } : l));
  const insPart = (l) => { const t = eff(l) * l.quantity; return !l.covered ? 0 : l.insCover === "" || l.insCover == null ? t : Math.min(Math.max(Number(l.insCover) || 0, 0), t); };
  const removeLine = (id) => setCart((c) => c.filter((l) => l.product_id !== id));

  // recompute prices when customer type changes
  useEffect(() => {
    setCart((c) => c.map((l) => {
      const p = products.find((x) => x.id === l.product_id);
      return p ? { ...l, unit_price: priceFor(p) } : l;
    }));
  }, [customerType, customerId, schemes, products]); // eslint-disable-line

  // Preload cart from a verified prescription (Send to POS)
  useEffect(() => {
    const rxId = searchParams.get("rx");
    if (!rxId || appliedRx.current || products.length === 0) return;
    appliedRx.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/prescriptions/${rxId}`);
        setFulfillment(data.fulfillment || "delivery");
        if (data.customer) {
          setCustomerType(data.customer.type || "cash");
          setPendingCustomerId(data.customer_id);
          if (data.customer.phone) setDeliveryPhone(data.customer.phone);
        }
        if (data.delivery_address) setDeliveryAddr(data.delivery_address);
        if (data.delivery_phone) setDeliveryPhone(data.delivery_phone);
        if (data.delivery_scheduled_for) setScheduledFor(data.delivery_scheduled_for.slice(0, 16));
        if (data.delivery_fee) setDeliveryFee(String(data.delivery_fee));
        const lines = (data.items || []).filter((it) => !it.rejected).map((it) => {
          const p = products.find((x) => x.id === it.product_id);
          return p ? { product_id: p.id, sku: p.sku, name: pName(p), schedule: p.drug_schedule, unit_price: p.price_cash, quantity: Number(it.quantity) || 1, covered: false } : null;
        }).filter(Boolean);
        if (lines.length) setCart(lines);
        toast.success("Prescription loaded — review and bill");
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [searchParams, products]); // eslint-disable-line

  // Apply the prescription's customer once its type's customer list has loaded
  useEffect(() => {
    if (pendingCustomerId && customers.some((c) => c.id === pendingCustomerId)) {
      setCustomerId(pendingCustomerId); setPendingCustomerId("");
    }
  }, [customers, pendingCustomerId]);

  const subtotal = cart.reduce((s, l) => s + eff(l) * l.quantity, 0);
  const coveredTotal = customerType === "insurance" ? cart.reduce((s, l) => s + insPart(l), 0) : 0;
  const fee = fulfillment === "delivery" ? Math.max(0, Number(deliveryFee) || 0) : 0;
  const outOfPocket = (customerType === "insurance" ? subtotal - coveredTotal : customerType === "corporate" ? 0 : subtotal) + (customerType === "corporate" ? 0 : fee);

  const submittingRef = useRef(false);
  const checkout = async () => {
    if (!branchId) return toast.error("Select a branch");
    if (!shift) return toast.error("Open a shift before making a sale");
    if (cart.length === 0) return toast.error("Cart is empty");
    if (customerType !== "cash" && !customerId) return toast.error(`Select a ${customerType} customer`);
    if (activeMembers.length && !memberId) return toast.error("Select the company member this sale is for");
    if (fulfillment === "delivery" && !deliveryAddr.trim()) return toast.error("Enter a delivery address");
    if (submittingRef.current) return;
    submittingRef.current = true;
    setPlacing(true);
    const idemKey = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    try {
      const { data } = await api.post("/orders", {
        branch_id: branchId, customer_type: customerType, customer_id: customerId || null,
        corporate_member_id: memberId || null,
        payment_method: paymentMethod, amount_tendered: Number(tendered) || 0,
        fulfillment, delivery_address: deliveryAddr, contact_phone: deliveryPhone, scheduled_for: scheduledFor,
        delivery_lat: deliveryLat, delivery_lng: deliveryLng, delivery_fee: fee,
        prescription_id: searchParams.get("rx") || null,
        lines: cart.map((l) => ({ product_id: l.product_id, quantity: l.quantity, covered: l.covered, discount_percent: l.discount || 0,
          insurance_amount: customerType === "insurance" && l.covered && l.insCover !== "" && l.insCover != null ? Number(l.insCover) || 0 : null })),
      }, { headers: { "Idempotency-Key": idemKey } });
      setReceipt(data); setCart([]); setTendered(""); setDeliveryAddr(""); setDeliveryPhone(""); setDeliveryFee(""); setDeliveryLat(null); setDeliveryLng(null); setScheduledFor(nowLocalDatetime()); setFulfillment("pickup");
      toast.success(`Dispensed ${data.invoice_no}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setPlacing(false); submittingRef.current = false; }
  };

  return (
    <div>
      <PageHeader title="Point of Sale" subtitle="Walk-in dispensing with FEFO batch allocation and customer-type pricing" />

      {/* Shift bar */}
      <Card className="p-3 mb-4 flex flex-wrap items-center justify-between gap-3 shadow-sm" data-testid="shift-bar">
        {shift ? (
          <>
            <div className="flex items-center gap-3 text-sm">
              <StatusBadge value="ok" label={`${shift.shift_label} shift open`} />
              <span className="text-muted-foreground">{shift.code} · {shift.cashier_name} · float {fmtGHS(shift.opening_float)}</span>
              <span className="text-muted-foreground">· {shift.totals?.sales_count || 0} sale(s) · cash {fmtGHS(shift.totals?.cash || 0)}</span>
            </div>
            <Button variant="outline" size="sm" data-testid="close-shift-btn" onClick={openCloseDlg}>Close shift & cash-up</Button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400"><ReceiptText className="h-4 w-4" /> No open shift — open one to start selling.</div>
            <Button size="sm" data-testid="open-shift-btn" disabled={!branchId} onClick={() => { setShiftLabel("Morning"); setOpeningFloat(""); setOpenShiftDlg(true); }}>Open shift</Button>
          </>
        )}
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: catalog */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-3 shadow-sm flex gap-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
              <Input data-testid="pos-search-input" placeholder="Search medicine by name / SKU…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
            </div>
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); changeBranch(v); }}>
              <SelectTrigger data-testid="pos-branch-select" className="w-48"><SelectValue placeholder="Location" /></SelectTrigger>
              <SelectContent>{branches.filter((b) => b.is_active !== false).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" size="icon" title="Record a sale lost to out-of-stock" data-testid="pos-missed-sale-btn" onClick={() => openMissed(null)}><PackageX className="h-4 w-4" /></Button>
          </Card>
          <ScanBox onScan={handleScan} placeholder="Scan a pack barcode to add to the sale…" />
          <MissedSaleDialog open={missedOpen} onOpenChange={setMissedOpen} branchId={branchId} source="pos" prefill={missedPrefill} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {products.map((p) => {
              const qty = stockMap[p.id] ?? 0;
              const out = qty <= 0;
              return (
              <button key={p.id} data-testid={`pos-add-${p.sku}`} onClick={() => out ? openMissed(p) : addToCart(p)}
                title={out ? "Out of stock — tap to record this as a missed sale" : ""}
                className={`text-left p-3 rounded-lg border transition-all ${out ? "border-amber-300 dark:border-amber-500/40 bg-amber-50/50 dark:bg-amber-500/5 hover:border-amber-500 cursor-pointer" : "border-border bg-card hover:border-primary hover:shadow-sm"}`}>
                <div className="flex items-start justify-between gap-1">
                  <span className="font-medium text-sm text-foreground leading-tight">{pName(p)}</span>
                  <StatusBadge value={p.drug_schedule} className="text-[10px] px-1" />
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{p.sku}</div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-sm font-mono font-semibold" style={{ color: "var(--mm-action)" }}>{fmtGHS(priceFor(p))}</span>
                  <span data-testid={`pos-stock-${p.sku}`} className={`text-[11px] font-medium ${out ? "text-amber-600" : "text-muted-foreground"}`}>{out ? "Out of stock · tap to log" : `${qty} in stock`}</span>
                </div>
              </button>
              );
            })}
            {products.length === 0 && <div className="col-span-full text-center text-muted-foreground py-10">No products</div>}
          </div>
        </div>

        {/* Right: cart */}
        <div className="lg:col-span-3">
          <Card className="p-4 shadow-sm sticky top-20">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-display font-semibold text-foreground">Current Sale</h3>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <Label className="text-xs">Customer type</Label>
                <Select value={customerType} onValueChange={setCustomerType}>
                  <SelectTrigger data-testid="pos-customer-type-select" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="corporate">Corporate</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {customerType !== "cash" && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">{customerType} customer</Label>
                    <button type="button" data-testid="pos-add-customer-btn" onClick={openAddCustomer}
                      className="text-[11px] font-medium inline-flex items-center gap-0.5 hover:underline" style={{ color: "var(--mm-action)" }}>
                      <UserPlus className="h-3 w-3" /> New
                    </button>
                  </div>
                  <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger data-testid="pos-customer-select" className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.scheme_member_no ? ` · ${c.scheme_member_no}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                  {activeMembers.length > 0 && (
                    <div className="mt-2">
                      <Label className="text-xs">Member <span className="text-rose-500">*</span></Label>
                      <Select value={memberId} onValueChange={setMemberId}>
                        <SelectTrigger data-testid="pos-member-select" className="h-9"><SelectValue placeholder="Who is this sale for?" /></SelectTrigger>
                        <SelectContent>{activeMembers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.staff_no ? ` · ${m.staff_no}` : ""}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mb-3">
              <Label className="text-xs">Fulfillment</Label>
              <Select value={fulfillment} onValueChange={setFulfillment}>
                <SelectTrigger data-testid="pos-fulfillment-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup">Direct sale (pickup)</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                </SelectContent>
              </Select>
              {fulfillment === "delivery" && (
                <div className="mt-2 space-y-2">
                  <Input data-testid="pos-delivery-address" placeholder="Delivery address" value={deliveryAddr} onChange={(e) => setDeliveryAddr(e.target.value)} className="h-9" />
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Delivery fee (GHS)</Label>
                    <Input data-testid="pos-delivery-fee" type="number" min={0} step="0.01" placeholder="0.00" value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)} className="h-9" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="pos-delivery-phone" placeholder="Contact phone" value={deliveryPhone} onChange={(e) => setDeliveryPhone(e.target.value)} className="h-9" />
                    <Input type="datetime-local" data-testid="pos-delivery-schedule" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} className="h-9" />
                  </div>
                  {/* Map pinning temporarily disabled — set to true to re-enable */}
                  {false && (
                  <div>
                    <Label className="text-xs">Pin exact delivery point (optional)</Label>
                    <LocationPicker
                      value={deliveryLat != null ? { lat: deliveryLat, lng: deliveryLng } : null}
                      center={(() => { const b = branches.find((x) => x.id === branchId); return b?.lat != null ? [b.lat, b.lng] : [5.6037, -0.187]; })()}
                      onChange={(lat, lng) => { setDeliveryLat(lat); setDeliveryLng(lng); }}
                    />
                  </div>
                  )}
                </div>
              )}
            </div>

            <div className="max-h-72 overflow-y-auto divide-y divide-border border-y border-border">
              {cart.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">Add items to begin</div>}
              {cart.map((l) => (
                <div key={l.product_id} className="py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{l.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{l.discount ? <span className="text-amber-600">{fmtGHS(eff(l))} ({l.discount}% off)</span> : `${fmtGHS(l.unit_price)} each`}{isNear(l.product_id) && <span className="ml-1 inline-block px-1 rounded bg-amber-100 text-amber-700 text-[10px] align-middle">near-expiry</span>}</div>
                    </div>
                    <button onClick={() => removeLine(l.product_id)} data-testid={`pos-remove-${l.sku}`} className="text-muted-foreground hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => setQty(l.product_id, -1)}><Minus className="h-3 w-3" /></Button>
                      <Input value={l.quantity} onChange={(e) => setQtyVal(l.product_id, e.target.value)} className="h-6 w-12 text-center px-1 text-xs" data-testid={`pos-qty-${l.sku}`} />
                      <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => setQty(l.product_id, 1)}><Plus className="h-3 w-3" /></Button>
                    </div>
                    <div className="flex items-center gap-2">
                      {isNear(l.product_id) && (
                        <label className="flex items-center gap-0.5 text-xs text-amber-600" title="Near-expiry discount">
                          <Input type="number" value={l.discount || 0} onChange={(e) => setDiscount(l.product_id, e.target.value)} data-testid={`pos-discount-${l.sku}`} className="h-6 w-14 text-center px-1 text-xs" />%
                        </label>
                      )}
                      {customerType === "insurance" && (
                        <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
                          <Checkbox checked={l.covered} onCheckedChange={() => toggleCovered(l.product_id)} data-testid={`pos-covered-${l.sku}`} /> covered
                        </label>
                      )}
                      {customerType === "insurance" && l.covered && (
                        <label className="flex items-center gap-1 text-[11px] text-sky-700" title="Amount insurance pays for this line — the rest is the client's top-up">
                          Ins. pays
                          <Input type="number" min={0} step="0.01" placeholder={(eff(l) * l.quantity).toFixed(2)} value={l.insCover ?? ""} onChange={(e) => setInsCover(l.product_id, e.target.value)} data-testid={`pos-ins-cover-${l.sku}`} className="h-6 w-20 text-right px-1 text-xs" />
                          {eff(l) * l.quantity - insPart(l) > 0.004 && <span className="text-amber-600 font-medium" data-testid={`pos-copay-${l.sku}`}>top-up {fmtGHS(eff(l) * l.quantity - insPart(l))}</span>}
                        </label>
                      )}
                      <span className="text-sm font-mono font-semibold">{fmtGHS(eff(l) * l.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 space-y-1 text-sm">
              <Row label="Subtotal" value={fmtGHS(subtotal)} />
              {customerType === "insurance" && <Row label="Insurance covers" value={fmtGHS(coveredTotal)} tone="sky" />}
              {customerType === "insurance" && cart.some((l) => l.covered && eff(l) * l.quantity - insPart(l) > 0.004) && <Row label="Client top-up (co-pay)" value={fmtGHS(cart.reduce((s, l) => s + (l.covered ? eff(l) * l.quantity - insPart(l) : 0), 0))} />}
              {fee > 0 && <Row label="Delivery fee" value={fmtGHS(fee)} />}
              {customerType === "corporate" && <Row label="On account (credit)" value={fmtGHS(subtotal + fee)} tone="violet" />}
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="font-semibold text-foreground">{customerType === "corporate" ? "Charged to account" : "Client pays"}</span>
                <span className="font-mono font-bold text-lg text-foreground" data-testid="pos-total">{fmtGHS(customerType === "corporate" ? subtotal + fee : outOfPocket)}</span>
              </div>
            </div>

            {customerType !== "corporate" && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <Label className="text-xs">Payment</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger data-testid="pos-payment-select" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="momo">Mobile Money</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {paymentMethod === "cash" && (
                  <div><Label className="text-xs">Tendered</Label><Input type="number" data-testid="pos-tendered-input" value={tendered} onChange={(e) => setTendered(e.target.value)} className="h-9" /></div>
                )}
              </div>
            )}

            <Button data-testid="pos-checkout-btn" className="w-full mt-4 h-10" onClick={checkout} disabled={placing || cart.length === 0 || !shift}>
              <ReceiptText className="h-4 w-4 mr-1.5" /> {placing ? "Processing…" : !shift ? "Open a shift to sell" : "Complete Sale"}
            </Button>
          </Card>
        </div>
      </div>

      {/* Receipt */}
      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Receipt · {receipt?.invoice_no}</DialogTitle></DialogHeader>
          {receipt && (
            <div className="text-sm">
              <div className="flex justify-between text-muted-foreground mb-2">
                <span>{receipt.customer_name}{receipt.corporate_member_name && <span className="block text-xs" data-testid="receipt-member">Member: {receipt.corporate_member_name}{receipt.corporate_member_staff_no ? ` (${receipt.corporate_member_staff_no})` : ""}</span>}</span><StatusBadge value={receipt.customer_type} />
              </div>
              <div className="divide-y divide-border border-y border-border">
                {receipt.lines.map((l, i) => (
                  <div key={i} className="py-2 flex justify-between">
                    <div><div className="font-medium">{l.generic_name} × {l.quantity}</div>
                      {l.copay_amount > 0 && <div className="text-xs text-amber-600">Insurance {fmtGHS(l.insurance_amount)} · client top-up {fmtGHS(l.copay_amount)}</div>}
                      <div className="text-xs text-muted-foreground font-mono">{l.allocations.map((a) => `${a.batch_no} (${a.quantity})`).join(", ")}</div>
                    </div>
                    <span className="font-mono">{fmtGHS(l.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-1">
                <Row label="Subtotal" value={fmtGHS(receipt.subtotal)} />
                <Row label="Tax" value={fmtGHS(receipt.tax)} />
                {receipt.delivery_fee > 0 && <Row label="Delivery fee" value={fmtGHS(receipt.delivery_fee)} />}
                {receipt.insurance_claim_amount > 0 && <Row label="Insurance claim (pending)" value={fmtGHS(receipt.insurance_claim_amount)} tone="sky" />}
                {receipt.on_account > 0 && <Row label="Charged to account" value={fmtGHS(receipt.on_account)} tone="violet" />}
                <div className="flex justify-between pt-2 border-t font-semibold"><span>Client paid</span><span className="font-mono">{fmtGHS(receipt.out_of_pocket)}</span></div>
                {receipt.change > 0 && <Row label="Change" value={fmtGHS(receipt.change)} />}
              </div>
              {receipt.delivery_tracking_token && (
                <div className="mt-3 rounded-lg p-3 text-xs" style={{ background: "var(--mm-surface-raised)" }}>
                  Delivery tracking link:<br />
                  <a className="font-mono break-all" style={{ color: "var(--mm-action)" }} href={`/track/${receipt.delivery_tracking_token}`} target="_blank" rel="noreferrer" data-testid="receipt-track-link">{window.location.origin}/track/{receipt.delivery_tracking_token}</a>
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <Button className="flex-1" onClick={() => { if (!printReceipt(receipt)) toast.error("Allow pop-ups to print the receipt"); }} data-testid="receipt-print-btn"><Printer className="h-4 w-4 mr-1.5" /> Print receipt</Button>
                <Button className="flex-1" variant="outline" onClick={() => setReceipt(null)} data-testid="receipt-done-btn">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick-add customer */}
      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New {customerType} customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name</Label><Input data-testid="pos-newcust-name" value={newCust.name} onChange={(e) => setNewCust((c) => ({ ...c, name: e.target.value }))} /></div>
            <div><Label>Phone</Label><Input data-testid="pos-newcust-phone" value={newCust.phone} onChange={(e) => setNewCust((c) => ({ ...c, phone: e.target.value }))} /></div>
            {customerType === "insurance" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Insurance scheme</Label>
                  <Select value={newCust.insurance_scheme_id} onValueChange={(v) => setNewCust((c) => ({ ...c, insurance_scheme_id: v }))}>
                    <SelectTrigger data-testid="pos-newcust-scheme"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{schemes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Insurance / member no.</Label><Input data-testid="pos-newcust-member" value={newCust.scheme_member_no} onChange={(e) => setNewCust((c) => ({ ...c, scheme_member_no: e.target.value }))} /></div>
              </div>
            )}
            {customerType === "corporate" && (
              <div><Label>Credit limit (GHS)</Label><Input type="number" data-testid="pos-newcust-credit" value={newCust.credit_limit} onChange={(e) => setNewCust((c) => ({ ...c, credit_limit: e.target.value }))} /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustOpen(false)}>Cancel</Button>
            <Button data-testid="pos-save-customer-btn" onClick={saveNewCustomer} disabled={savingCust}>{savingCust ? "Saving…" : "Add & select"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Open shift */}
      <Dialog open={openShiftDlg} onOpenChange={setOpenShiftDlg}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Open shift</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Shift</Label>
              <Select value={shiftLabel} onValueChange={setShiftLabel}>
                <SelectTrigger data-testid="open-shift-label"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Morning">Morning</SelectItem>
                  <SelectItem value="Afternoon">Afternoon</SelectItem>
                  <SelectItem value="Night">Night</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Opening cash float (GHS)</Label><Input type="number" data-testid="open-shift-float" value={openingFloat} onChange={(e) => setOpeningFloat(e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpenShiftDlg(false)}>Cancel</Button><Button data-testid="confirm-open-shift-btn" onClick={doOpenShift}>Open shift</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift summary after close */}
      <Dialog open={!!closedShift} onOpenChange={(o) => !o && setClosedShift(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Shift summary</DialogTitle></DialogHeader>
          {closedShift && <ShiftSummary shift={closedShift} />}
          <DialogFooter><Button onClick={() => setClosedShift(null)} data-testid="shift-summary-done">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close shift / cash-up */}
      <Dialog open={closeShiftDlg} onOpenChange={setCloseShiftDlg}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Close shift & cash-up</DialogTitle></DialogHeader>
          {shift && (
            <div className="space-y-3 text-sm">
              <div className="text-muted-foreground">{shift.code} · {shift.shift_label} · {shift.cashier_name}</div>
              <div className="rounded-lg border border-border divide-y divide-border">
                <Row label="Sales count" value={shift.totals?.sales_count || 0} />
                <Row label="Cash sales" value={fmtGHS(shift.totals?.cash || 0)} />
                <Row label="Card sales" value={fmtGHS(shift.totals?.card || 0)} />
                <Row label="MoMo sales" value={fmtGHS(shift.totals?.momo || 0)} />
                <Row label="Insurance (claims)" value={fmtGHS(shift.totals?.insurance || 0)} tone="sky" />
                <Row label="Corporate credit" value={fmtGHS(shift.totals?.corporate_credit || 0)} tone="violet" />
                <Row label="Opening float" value={fmtGHS(shift.opening_float || 0)} />
                <div className="flex items-center justify-between px-1 py-1.5 font-medium"><span>Expected cash in drawer</span><span className="font-mono">{fmtGHS(expectedCash)}</span></div>
              </div>
              <div><Label>Counted cash (GHS)</Label><Input type="number" data-testid="close-counted-cash" value={countedCash} onChange={(e) => setCountedCash(e.target.value)} /></div>
              <div className={`flex justify-between font-medium ${liveVariance === 0 ? "text-emerald-600" : "text-rose-600"}`}><span>Variance</span><span className="font-mono">{fmtGHS(liveVariance)}</span></div>
              <div><Label>Note (optional)</Label><Input data-testid="close-shift-note" value={closeNote} onChange={(e) => setCloseNote(e.target.value)} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setCloseShiftDlg(false)}>Cancel</Button><Button data-testid="confirm-close-shift-btn" onClick={doCloseShift}>Close shift</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, tone }) {
  const c = tone === "sky" ? "text-sky-700" : tone === "violet" ? "text-violet-700" : "text-muted-foreground";
  return <div className="flex items-center justify-between"><span className={c}>{label}</span><span className="font-mono text-foreground">{value}</span></div>;
}
